// Decoder events carry an optional `_streamMeta.id` (SSE last-event-id, used
// by the orchestrator to populate the Last-Event-ID header on reconnect).
// retry: is not surfaced here — see DecodeOptions.onRetry below for why.
//
// `_streamMeta` is a reserved key on yielded events. Consumer types passed as
// `T` should not define their own `_streamMeta` property; the intersection
// will silently overwrite it whenever the decoder has metadata to attach.
// The name is deliberately specific (rather than the more generic `_meta`)
// to minimize the chance of collision with consumer domain types.
export type DecodedEvent<T = unknown> = T & {
  _streamMeta?: { id?: string };
};

// Wrapper shape for events whose payload is not a plain object: SSE comments
// yielded as keepalive, primitive JSON values, and parseEventData:false raw
// strings. Object payloads spread to the event root instead — see decodeSse
// docs below.
export type WrapperEvent = {
  type: string;
  data: unknown;
  _streamMeta?: { id?: string };
};

// Keepalive (heartbeat) event yielded for SSE comment lines. Carries a brand
// so it's discriminable from a server-sent `event: keepalive` data event,
// which would also appear as `{ type: "keepalive", data: ... }`. Consumers
// filter heartbeats via `"__keepalive" in event` or `event.__keepalive`.
export type KeepaliveEvent = {
  type: "keepalive";
  data: string;
  __keepalive: true;
  _streamMeta?: { id?: string };
};

// Errors thrown by the decoder when the wire format is malformed (e.g. JSON
// payload won't parse). Caught and re-thrown by the orchestrator as a
// non-retryable error — these are server-side bugs we want to surface, not
// transient network failures we want to reconnect through.
export class StreamDecodeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "StreamDecodeError";
  }
}

export type DecodeOptions = {
  parseEventData?: boolean; // default true — JSON.parse each event payload
  // When provided and aborted, the decoder cancels its in-flight read via
  // reader.cancel() so the for-await loop unblocks promptly instead of waiting
  // for the next chunk (which may never arrive if the source has gone quiet).
  signal?: AbortSignal;
  // Called when the decoder parses a `retry: <ms>` field. Per the SSE spec,
  // retry is a connection-level reconnection-time hint, not per-event data —
  // so we route it out-of-band rather than yielding a synthetic event the
  // consumer would have to filter. The orchestrator uses it to update its
  // backoff before the next reconnect attempt.
  onRetry?: (ms: number) => void;
};

const DEFAULT_PARSE: Required<Pick<DecodeOptions, "parseEventData">> = {
  parseEventData: true,
};

// SSE event accumulator. The WHATWG SSE algorithm dispatches on blank line,
// so we collect fields here until that signal arrives.
type SseEventBuilder = {
  eventType: string;
  data: string[];
  id: string | undefined;
};

function newSseEvent(): SseEventBuilder {
  return { eventType: "message", data: [], id: undefined };
}

// Decodes a Server-Sent Events stream into JS events. Implements the WHATWG
// SSE subset that's actually used in practice: data, event, id, retry,
// blank-line dispatch, ':' comments. Multi-line data is joined with '\n'
// before JSON.parse. Comment lines are yielded as keepalive events so callers
// can render heartbeat indicators. Last-event-id memory propagates to
// subsequent events that don't carry their own id, per the SSE spec.
//
// Yielded shape depends on the payload:
// - Object payloads (the typical case) spread to the event root with
//   `_streamMeta` added alongside, and `type` injected from `event:` if not
//   already present on the payload. The consumer sees their domain shape
//   directly.
// - Non-object payloads (primitives, parseEventData:false raw strings)
//   are wrapped as `{ type, data, _streamMeta? }`.
// - SSE comment lines yield a `KeepaliveEvent` — same shape as a wrapper
//   but with a `__keepalive: true` brand for type-safe discrimination.
//
// `retry:` updates do not yield events — they're delivered via the
// `onRetry` callback. This matches WHATWG semantics (retry is a connection-
// level hint, not message data) and avoids dispatching empty `{}` payloads
// for retry-only frames.
export async function* decodeSse<T = unknown>(
  stream: ReadableStream<Uint8Array>,
  opts: DecodeOptions = {}
): AsyncGenerator<DecodedEvent<T> | WrapperEvent | KeepaliveEvent> {
  const { parseEventData } = { ...DEFAULT_PARSE, ...opts };
  const onRetry = opts.onRetry;
  const reader = stream.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  let event = newSseEvent();
  let lastEventIdMemory: string | undefined;

  // Wire signal-driven cancellation. reader.cancel() is the only thing that
  // unblocks an in-flight read() when the source has gone quiet (a bare
  // stream.cancel() would error because the reader holds the lock).
  let removeAbortListener: (() => void) | undefined;
  if (opts.signal) {
    if (opts.signal.aborted) {
      reader.cancel().catch(() => {});
    } else {
      const onAbort = () => reader.cancel().catch(() => {});
      opts.signal.addEventListener("abort", onAbort, { once: true });
      removeAbortListener = () =>
        opts.signal!.removeEventListener("abort", onAbort);
    }
  }

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let newlineIdx: number;
      while ((newlineIdx = buffer.indexOf("\n")) !== -1) {
        const line = buffer.slice(0, newlineIdx).replace(/\r$/, "");
        buffer = buffer.slice(newlineIdx + 1);

        if (line === "") {
          // Per spec, the last-event-id memory is updated on blank line even
          // when dispatch is skipped (e.g. id-only frames). This lets clients
          // send `id: N\n\n` to advance the cursor without emitting an event.
          if (event.id !== undefined) lastEventIdMemory = event.id;
          // Blank line dispatches the accumulated event (if any).
          const dispatched = dispatchSseEvent<T>(
            event,
            parseEventData,
            lastEventIdMemory
          );
          if (dispatched !== undefined) yield dispatched;
          event = newSseEvent();
        } else if (line.startsWith(":")) {
          // Comment / keepalive — yield so callers can render heartbeats. The
          // `__keepalive: true` brand distinguishes a real heartbeat from a
          // wire-emitted `event: keepalive\ndata: ...` frame (which would
          // also produce `type: "keepalive"`).
          const keepalive: KeepaliveEvent = {
            type: "keepalive",
            data: line.slice(1).trim(),
            __keepalive: true,
          };
          yield keepalive;
        } else {
          parseSseField(line, event, onRetry);
        }
      }
    }

    // Flush decoder state. No final dispatch on EOF — SSE spec only dispatches
    // on blank line, and an unterminated trailing event is ignored.
    buffer += decoder.decode();
  } finally {
    removeAbortListener?.();
    // cancel() releases the lock and cancels any in-flight read; releaseLock()
    // would throw if a read() were pending (which happens when the consumer
    // breaks out of `for await` or calls gen.return() during a read). The
    // .catch(() => {}) swallows upstream cancellation propagation so the
    // cleanup path itself never throws.
    await reader.cancel().catch(() => {});
  }
}

function parseSseField(
  line: string,
  event: SseEventBuilder,
  onRetry: ((ms: number) => void) | undefined
): void {
  const colonIdx = line.indexOf(":");
  let field: string;
  let valueRaw: string;
  if (colonIdx === -1) {
    // A line with no colon is treated as a field name with empty value.
    field = line;
    valueRaw = "";
  } else {
    field = line.slice(0, colonIdx);
    valueRaw = line.slice(colonIdx + 1);
  }
  // Per spec, a single leading space in the value is stripped.
  const value = valueRaw.startsWith(" ") ? valueRaw.slice(1) : valueRaw;

  switch (field) {
    case "event":
      event.eventType = value;
      break;
    case "data":
      event.data.push(value);
      break;
    case "id":
      // Null character is forbidden in SSE id; ignore the field if present.
      if (!value.includes("\0")) event.id = value;
      break;
    case "retry": {
      // Per spec, retry updates the reconnection time but does not contribute
      // to the dispatched event. Hand it to the orchestrator out-of-band.
      const ms = Number(value);
      if (Number.isFinite(ms) && ms >= 0) onRetry?.(ms);
      break;
    }
    default:
      // Unknown fields ignored per spec.
      break;
  }
}

function dispatchSseEvent<T>(
  event: SseEventBuilder,
  parseEventData: boolean,
  lastEventIdMemory: string | undefined
): DecodedEvent<T> | WrapperEvent | undefined {
  // Per WHATWG, an event with an empty data buffer is not dispatched. id-only
  // and retry-only frames update internal state (id memory, retry callback)
  // but yield nothing to the consumer.
  if (event.data.length === 0) {
    return undefined;
  }

  const dataStr = event.data.join("\n");
  let payload: unknown;

  if (parseEventData) {
    try {
      payload = JSON.parse(dataStr);
    } catch {
      throw new StreamDecodeError(
        `decodeSse: malformed JSON: ${dataStr.slice(0, 200)}`
      );
    }
  } else {
    payload = dataStr;
  }

  // Compose stream meta. Use the event's own id, or fall back to last-event-id
  // memory.
  const idForMeta = event.id ?? lastEventIdMemory;
  const streamMeta: { id?: string } = {};
  if (idForMeta !== undefined) streamMeta.id = idForMeta;
  const hasStreamMeta = Object.keys(streamMeta).length > 0;

  if (payload !== null && typeof payload === "object") {
    // Object payload becomes the root. Only inject `type` from `event:` if the
    // payload doesn't already carry its own.
    const payloadObj = payload as Record<string, unknown>;
    const injectType =
      event.eventType !== "message" && payloadObj.type === undefined;
    return {
      ...payloadObj,
      ...(injectType ? { type: event.eventType } : {}),
      ...(hasStreamMeta ? { _streamMeta: streamMeta } : {}),
    } as DecodedEvent<T>;
  }

  // Primitive payload (string when parseEventData=false, or JSON primitives):
  // wrap it.
  const wrapper: WrapperEvent = {
    type: event.eventType,
    data: payload,
    ...(hasStreamMeta ? { _streamMeta: streamMeta } : {}),
  };
  return wrapper;
}
